import { useEffect, useRef } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { AlertCircle, Plus } from 'lucide-react';
import SingleSelectInput from '@/components/form/SingleSelectInput.tsx';
import ToggleInput from '@/components/form/ToggleInput.tsx';
import { DateInput } from '@/components/form/DateInput.tsx';
import TimeInput from '@/components/form/TimeInput.tsx';
import { TextareaInput } from '@/components/form/TextareaInput.tsx';
import ClientSearchSelectRHF from '@/components/asyncSelect/entitySelects/ClientSearchSelectRHF.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import InputLabel from '@/components/form/base/InputLabel.tsx';
import TwoInputsRow from '@/components/form/ui/TwoInputsRow.tsx';
import { SmailTitle } from '@/components/ui/Title.tsx';
import { CardContent } from '@/components/ui/Card.tsx';
import { ValueInput } from '@/components/form/ValueInput.tsx';
import Chip from '@/components/ui/Chip.tsx';
import Dot from '@/components/ui/Dot.tsx';
import AllocationRow from '@/features/dashboard/financialTransactions/components/AllocationRow.tsx';
import {
  FinancialTransactionInputData,
  getUnallocatedAmount,
} from '@/features/dashboard/financialTransactions/schemas/financialTransactionSchema.ts';
import { TransactionDirection } from '@/features/dashboard/financialTransactions/types/TransactionDirection.ts';
import {
  allocationTypeForDirection,
  transactionDirectionLabels,
  transactionDirectionOptions,
} from '@/features/dashboard/financialTransactions/types/TransactionDirection.utils.ts';
import { currencySelectOptions } from '@/features/dashboard/paymentConfig/types/Currency.utils.ts';
import { getPossiblePaymentsMethods } from '@/features/dashboard/tourConfig/types/PaymentMethod.utils.ts';
import { Currency } from '@/features/dashboard/paymentConfig/types/Currency.ts';
import { formatPrice } from '@/utils/formatters.ts';
import { cn } from '@/lib/utils.ts';
import { ALLOCATIONS_ERROR_ID } from '@/features/dashboard/financialTransactions/components/allocationsError.ts';

interface FinancialTransactionFormProps {
  /** W edycji kierunek jest zamknięty, a „Sposób zapłaty” tylko do odczytu. */
  mode: 'create' | 'edit';
  /**
   * `MarkAsPaidModal` wchodzi w tryb `edit`, choć transakcja dopiero powstaje:
   * podpowiedź „po utworzeniu transakcji” byłaby tam nieprawdą, a sposobu
   * zapłaty nikt jeszcze nie wybrał, więc nie ma czego pokazywać.
   */
  isSavedTransaction?: boolean;
  /** Wejście z akcji „Rozdysponuj resztę”: przewija od razu do przypisań. */
  focusAllocations?: boolean;
}

const FinancialTransactionForm = ({
  mode,
  isSavedTransaction = mode === 'edit',
  focusAllocations = false,
}: FinancialTransactionFormProps) => {
  const {
    control,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<FinancialTransactionInputData>();

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'allocations',
  });

  const direction = (watch('direction') ??
    TransactionDirection.INCOMING) as TransactionDirection;
  const clientId = (watch('clientId') ?? '') as string;
  const currency = (watch('currency') ?? Currency.PLN) as Currency;
  const amount = watch('amount') as number;
  const allocations = watch('allocations') ?? [];
  const isOutgoing = direction === TransactionDirection.OUTGOING;

  const allocationsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focusAllocations) {
      allocationsRef.current?.scrollIntoView({ block: 'center' });
    }
  }, [focusAllocations]);

  const remaining = getUnallocatedAmount(amount, allocations);
  const isOverAllocated = remaining < 0;

  // `min(1)` na tablicy ma w Zodzie ścieżkę `['allocations']`, więc `zodResolver`
  // zapisuje go jako `errors.allocations.message`; pod `root` trafia dopiero
  // błąd z jawnej ścieżki `['allocations', 'root']` (suma ponad kwotę).
  const allocationsError =
    errors.allocations?.root?.message ?? errors.allocations?.message;

  const emptyAllocation = (
    nextDirection: TransactionDirection,
    nextAmount: number,
  ) => ({
    type: allocationTypeForDirection(nextDirection),
    bookingId: '',
    expensePlannedTransactionId: '',
    amount: nextAmount,
  });

  const resetAllocations = (nextDirection: TransactionDirection) =>
    replace([emptyAllocation(nextDirection, 0)]);

  return (
    <>
      {mode === 'create' ? (
        <ToggleInput
          fieldPath="direction"
          label="Rodzaj transakcji"
          required
          options={transactionDirectionOptions}
          control={control}
          error={errors.direction?.message}
          onChange={(value) => {
            const next = value as TransactionDirection;
            // Zmiana kierunku unieważnia przypisania: mają inny kształt i inny znak.
            resetAllocations(next);
            if (next === TransactionDirection.OUTGOING) {
              setValue('clientId', '');
            }
          }}
        />
      ) : (
        <div className="flex flex-col gap-1.5">
          <InputLabel label="Rodzaj transakcji" />
          <Chip
            tone="accent"
            fill="soft"
            label={transactionDirectionLabels[direction]}
          />
        </div>
      )}

      {!isOutgoing && (
        <ClientSearchSelectRHF
          fieldPathName="clientId"
          control={control}
          label="Zapisujący"
          required
          isDisabled={mode === 'edit'}
          hint={
            isSavedTransaction
              ? 'Zapisującego nie da się zmienić po utworzeniu transakcji'
              : undefined
          }
          error={errors.clientId?.message}
          placeholder="Wyszukaj zapisującego..."
          // Rezerwacje należą do klienta, więc zmiana klienta zeruje przypisania.
          onChange={() => resetAllocations(direction)}
        />
      )}

      <TwoInputsRow>
        <div className="flex flex-row items-end gap-1">
          <DateInput
            dateFieldName="transactionDate"
            control={control}
            label="Data transakcji"
            required
            error={errors.transactionDate?.message}
          />
          <TimeInput
            {...register('transactionTime')}
            error={errors.transactionTime?.message}
          />
        </div>
      </TwoInputsRow>

      <TwoInputsRow>
        {/* Siatka, nie `items-end`: wyrównanie do dołu ustawiało select równo z
            podpowiedzią pod polem kwoty, a nie z samym polem. */}
        <div className="grid grid-cols-[minmax(0,1fr)_120px] items-start gap-1">
          <ValueInput
            label="Kwota"
            register={register('amount', { valueAsNumber: true })}
            placeholder="0"
            required
            type="number"
            step={0.01}
            error={errors.amount?.message}
            hint="Wpisz kwotę dodatnią, znak nada system"
            sufix={currency}
          />
          <SingleSelectInput
            fieldName="currency"
            control={control}
            options={currencySelectOptions}
            label="Waluta"
            required
            error={errors.currency?.message}
          />
        </div>
      </TwoInputsRow>

      {(mode === 'create' || isSavedTransaction) && (
        <SingleSelectInput
          fieldName="paymentMethod"
          control={control}
          options={getPossiblePaymentsMethods()}
          label="Sposób zapłaty"
          required
          isDisabled={mode === 'edit'}
          hint={
            mode === 'edit'
              ? 'Sposobu zapłaty nie da się zmienić po zaksięgowaniu transakcji'
              : undefined
          }
          error={errors.paymentMethod?.message}
        />
      )}

      <TextareaInput
        label="Opis"
        register={register('description')}
        error={errors.description?.message}
        placeholder="Opcjonalny opis transakcji..."
      />

      <div className="-mx-4 border-t border-neutral-200" ref={allocationsRef}>
        <CardContent>
          <div className="space-y-1">
            <SmailTitle>Przypisz transakcję</SmailTitle>
            <div className="text-sm text-neutral-600">
              {isOutgoing
                ? 'Rozdziel kwotę na zaplanowane wydatki'
                : 'Rozdziel kwotę na rezerwacje klienta'}
            </div>
          </div>
        </CardContent>

        <div className="space-y-2 bg-neutral-50 px-4 py-3">
          {fields.map((field, index) => (
            <AllocationRow
              key={field.id}
              index={index}
              mode={mode}
              direction={direction}
              clientId={clientId}
              currency={currency}
              onRemove={() => remove(index)}
            />
          ))}

          <div className="flex flex-row flex-wrap items-center justify-between gap-3 pt-0.5">
            <ActionButton
              tone="ghost"
              size="sm"
              type="button"
              onClick={() =>
                append(
                  emptyAllocation(direction, remaining > 0 ? remaining : 0),
                )
              }
            >
              <Plus className="size-3.5" />
              Dodaj przypisanie
            </ActionButton>

            <div className="flex flex-row items-center gap-2">
              <Dot size="md" tone={isOverAllocated ? 'error' : 'neutral'} />
              <span
                className={cn(
                  'text-[13px] leading-[18px]',
                  isOverAllocated ? 'text-error-300' : 'text-neutral-600',
                )}
              >
                Pozostało do rozdysponowania:{' '}
                <span className="font-bold">
                  {formatPrice(remaining, currency)}
                </span>
              </span>
            </div>
          </div>

          {allocationsError && (
            <div
              id={ALLOCATIONS_ERROR_ID}
              className="flex flex-row items-start gap-2.5 rounded-[10px] border border-error-100 bg-error-50 p-3"
            >
              <AlertCircle
                className="mt-px size-4 shrink-0 text-error-200"
                aria-hidden
              />
              <p className="text-[13px] leading-[18px] text-error-300">
                {allocationsError}
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default FinancialTransactionForm;
